import {
  Fragment,
  ReactNode,
  useEffect,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  Breadcrumbs,
  SkeletonLoader,
  PageStatus,
  SkeletonBase,
} from '@unified-checkout/design-system';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';
import { Google } from '@unified-checkout/design-system/icons/digital_platforms/other/index';

import { MultiSectionCard } from '@app/dashboard/components/inner_layout/MultiSectionCard';
import {
  BREADCRUMBS_LINKS,
  CONNECT_ACTION,
  DISCONNECT_ACTION,
  GOOGLE
} from '@app/dashboard/constants/Bills';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getAccountDetails } from '@app/dashboard/services/inner_layout/bills/pay_ads/account-details';
import { useLangStore } from '@app/dashboard/store/lang';
import * as amplitude from '@amplitude/analytics-browser';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/bills';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';

import { ConnectDisconnectModal } from './Modals/Connect-Disconnect';
import { TopUpModal } from './Modals/TopUp';
import styles from './pay-ads.module.scss';

export const PayAds = () => {
  const { t } = useTranslation();
  const lang = useLangStore();
  const navigate = useNavigate();

  const [showTopUp, setShowTopUp] = useState(false);
  const [showConnectDisconnect, setShowConnectDisconnect] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [actionType, setActionType] = useState<string>(CONNECT_ACTION);

  const {
    data: accountDetails,
    isPending: isAccountDetailsLoading,
    error: accountDetailsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_ACCOUNT_DETAILS, lang],
    queryFn: getAccountDetails,
  });


  const {
    account_email: mainAccountEmail,
    ad_accounts,
  } = accountDetails?.data?.data?.account_details ?? {};


  useEffect(() => {
    if (accountDetails && !isAccountDetailsLoading) {
      amplitude.track(BILLS_EVENTS.BILLS_CONVERTED_PAY_HOME_PAGE_VIEWED, {
        [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
        [BILLS_EVENTS_PARAMS.CONNECTED_ACCOUNTS_COUNT]: ad_accounts?.filter((a: any) => a.connected)?.length,
        [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: accountDetails.data.data.account_details.account_id,
        [BILLS_EVENTS_PARAMS.STATUS]: accountDetails?.data?.status,
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: (accountDetailsError as any)?.response?.data?.message,
      });
    }
  }, [accountDetails, isAccountDetailsLoading, ad_accounts, accountDetailsError]);

  const btnProps = (isConnected: string, account: any) => [
    ...(isConnected
      ? [
        {
          text: t('innerLayout.billsPage.payAds.topUp'),
          onClickHandler: () => {
            amplitude.track(BILLS_EVENTS.BILLS_TOP_UP_AD_ACCOUNT_SELECTED, {
              [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
              [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: account?.account_id,
              [BILLS_EVENTS_PARAMS.STATUS]: accountDetails?.data?.status,
              [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: (accountDetailsError as any)?.response?.data?.message,
            });
            setSelectedAccount(account);
            setShowTopUp(true);
          },
        },
        {
          color: 'danger' as const,
          variant: 'link' as const,
          text: t('innerLayout.billsPage.payAds.disconnect'),
          onClickHandler: () => {
            amplitude.track(BILLS_EVENTS.BILLS_DISCONNECT_AD_ACCOUNT_SELECTED, {
              [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
              [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: account?.account_id,
              [BILLS_EVENTS_PARAMS.STATUS]: accountDetails?.data?.status,
              [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: (accountDetailsError as any)?.response?.data?.message,
            });
            setSelectedAccount(account);
            setActionType(DISCONNECT_ACTION);
            setShowConnectDisconnect(true);
          },
        },
      ]
      : [
        {
          variant: 'outlined' as const,
          text: t('innerLayout.billsPage.payAds.connect'),
          onClickHandler: () => {
            amplitude.track(BILLS_EVENTS.BILLS_CONNECT_AD_ACCOUNT_FROM_SETTINGS_SELECTED, {
              [BILLS_EVENTS_PARAMS.AD_PLATFORM]: GOOGLE,
              [BILLS_EVENTS_PARAMS.AD_ACCOUNT_ID]: account?.account_id,
              [BILLS_EVENTS_PARAMS.STATUS]: accountDetails?.data?.status,
              [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: (accountDetailsError as any)?.response?.data?.message,
            });
            setSelectedAccount(account);
            setActionType(CONNECT_ACTION);
            setShowConnectDisconnect(true);
          },
        },
      ]),
  ];

  const cardSkeletonLoader = ({
    section,
    height,
    key,
    isLoading,
  }: {
    section: ReactNode;
    height: string;
    noOfRows: number;
    key: string;
    isLoading: boolean;
  }): ReactNode => {
    return isLoading ? (
      <SkeletonBase
        height={height}
        key={key}
      />
    ) : (
      <Fragment key={key}>{section}</Fragment>
    );
  };

  const visibleAccounts = isAccountDetailsLoading
    ? Array.from({ length: 3 }, (_, index) => ({
      id: index,
    }))
    : ad_accounts ?? [];

  return (
    <div className={styles['pay-ads-wrapper']}>
      <div className={styles['pay-ads-wrapper__header']}>
        <Breadcrumbs linksProps={BREADCRUMBS_LINKS(t, navigate)} />

        <div className={styles['pay-ads-wrapper__header__title']}>
          {t('innerLayout.billsPage.payAds.title')}
        </div>

        {cardSkeletonLoader({
          section: (
            <MultiSectionCard
              title="Google Account"
              subtitle={mainAccountEmail}
              icon={Google}
            />
          ),
          height: '6rem',
          noOfRows: 2,
          key: 'main-account',
          isLoading: isAccountDetailsLoading,
        })}
      </div>

      <div className={styles['pay-ads-wrapper__cards-wrapper']}>
        <div className={styles['pay-ads-wrapper__cards-wrapper__header']}>
          {t('innerLayout.billsPage.payAds.addAccounts')}
        </div>

        <div className={styles['pay-ads-wrapper__cards-wrapper__cards']}>
          {visibleAccounts.length > 0 ? (
            visibleAccounts.map((account: any, index: number) => (
              <div
                className={
                  styles['pay-ads-wrapper__cards-wrapper__cards__card']
                }
                key={account?.account_id || account?.id || index}
              >
                {cardSkeletonLoader({
                  height: '9rem',
                  noOfRows: 2,
                  key: String(
                    account?.account_id || account?.id || index
                  ),
                  isLoading: isAccountDetailsLoading,
                  section: (
                    <MultiSectionCard
                      title={account?.name}
                      subtitle={account?.account_id}
                      icon={account?.icon}
                      btnProps={btnProps(account?.connected, account)}
                    />
                  ),
                })}
              </div>
            ))
          ) : (
            <div className={styles['pay-ads-wrapper__cards-wrapper__empty']}>
              <PageStatus
                status="error"
                title={t('innerLayout.billsPage.payAds.noAccountsFound')}
                description={t(
                  'innerLayout.billsPage.payAds.noAccountsFoundDescription'
                )}
                icon={CircleExclamation}
              />
            </div>
          )}
        </div>
      </div>

      <TopUpModal
        showTopUp={showTopUp}
        setShowTopUp={setShowTopUp}
        accountDetails={accountDetails}
        selectedAccount={selectedAccount}
      />

      <ConnectDisconnectModal
        showConnectDisconnect={showConnectDisconnect}
        setShowConnectDisconnect={setShowConnectDisconnect}
        account={selectedAccount}
        action={actionType}
      />
    </div>
  );
};